import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
  Button,
  CircularProgress,
  Box,
  Alert,
  AlertTitle,
  Tooltip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { Lightbulb, MoreHoriz, Save } from '@mui/icons-material'

import ContactList from './components/ContactList/ContactList'
import CustomerOpportunity from './components/CustomerOpportunity/CustomerOpportunity'
import CustomerInfo from './CustomerInfo'
import CustomFields from './components/CustomFields/CustomFields'
import useCustomerOpportunities from '../hooks/api/useCustomerOpportunities'
import SplitButton from '../common/SplitButton'
import CustomerLocations from './components/CustomerLocations/CustomerLocations'
import useCustomerLocations from '../hooks/api/useCustomerLocations'
import useAlert from '../hooks/useAlert'
import { authedAxios, debounce } from '../utils'
import PipelineSummary from './components/PipelineSummary/PipelineSummary'
import PipleineSelectionModal from './components/PipelineSelectionModal/PipelineSelectionModal'
import useCustomerPipelineSummary from '../hooks/api/useCustomerPipelineSummary'
import CustomerRevenueSummary from './components/CustomerRevenueSummary'
import DeleteConfirmModal from './components/DeleteConfirmModal'
import useCompanyConfig from '../hooks/api/useCompanyConfig'
import CustomerHeader from './components/CustomerHeader'
import usePrompt from '../utils/navigation'
import { styled } from '@mui/material/styles'
import { tooltipClasses } from '@mui/material/Tooltip'
import DuplicateNameModal from './components/DuplicateNameModal'

export const NoMaxWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
})

const SaveButton = ({ saveIsDisabled, isNew, submitting, saveAll }) => {
  return (
    <Box sx={{ flexShrink: 0, mt: 1, mr: 2 }}>
      {saveIsDisabled ? (
        <NoMaxWidthTooltip
          placement='bottom-end'
          title={
            <Typography sx={{ fontSize: '18px' }}>
              Fill out all required fields highlighted in red
            </Typography>
          }
        >
          <span>
            <Button
              disabled={saveIsDisabled}
              color='primary'
              variant='contained'
              onClick={saveAll}
              endIcon={<Save />}
            >
              {isNew ? 'Save' : 'Save all'}
            </Button>
          </span>
        </NoMaxWidthTooltip>
      ) : (
        <Button
          disabled={submitting}
          color='primary'
          variant='contained'
          onClick={saveAll}
          endIcon={<Save />}
        >
          {isNew ? 'Save' : 'Save all'}
        </Button>
      )}
    </Box>
  )
}

const CustomerDetail = (props) => {
  const {
    loading,
    dropdowns,
    types,
    customFields,
    customFieldTypes,
    pipelines,
    products,
    billingCycleTypes,
    setTab,
    tabs,
    customerQuery,
    customerStatusTypesData,
    customerContactsQuery,
    customerTicketsQuery,
    tasks,
  } = props

  const { id } = useParams()
  const isNew = id === 'new'
  const navigate = useNavigate()

  const [isDirty, setIsDirty] = useState(false)

  const [isCreatingOpportunity, setIsCreatingOpportunity] = useState(false)
  const [isSelectingPipeline, setIsSelectingPipeline] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false)

  const [expandedAccordions, setExpandedAccordions] = useState({
    opportunity: false,
    info: true,
    historic: false,
    current: false,
    potential: false,
    custom: false,
    contacts: false,
    locations: false,
  })

  const collapseAll = () => {
    setExpandedAccordions((prev) => {
      const newVal = {}

      Object.keys(prev).forEach((key) => (newVal[key] = false))

      return newVal
    })
  }

  const { createAlert, createGenericErrorAlert } = useAlert()

  const {
    customer,
    setCustomer,
    saveCustomer,
    archiveCustomer,
    getCustomer,
    ..._customer
  } = customerQuery

  const [customerCopy, setCustomerCopy] = useState(null)

  const [duplicateName, setDuplicateName] = useState(false)

  useEffect(() => {
    if (_customer.isLoading) return
    setCustomerCopy(JSON.stringify(customer))
  }, [_customer.isLoading])

  useEffect(() => {
    if (!customer) return
    if (_customer.isLoading) return

    setIsDirty(JSON.stringify(customer) !== customerCopy)
  }, [customer])

  const {
    customerContacts,
    setCustomerContacts,
    commitContact,
    removeContact,
    saveContacts,
    ..._customerContacts
  } = customerContactsQuery

  const {
    currentOpportunity,
    setCurrentOpportunity,
    saveCurrentOpportunity,
    closeOpportunity,
    deleteOpportunity,
    getCustomerOpportunities,
    ..._customerOpportunities
  } = useCustomerOpportunities(customer.id || id, {
    isNewOpportunity: isCreatingOpportunity,
    isNewCustomer: isNew,
    pipelines,
  })

  const {
    customerLocations,
    saveCustomerLocations,
    setCustomerLocations,
    primaryLocation,
    deleteLocation,
    ..._customerLocations
  } = useCustomerLocations(id)

  const handleLocationChange = (key, value) => {
    const pl = customerLocations.find((l) => l.isDefault)

    if (pl) {
      return setCustomerLocations((prev) =>
        prev.map((l) => {
          if (l.isDefault) {
            return { ...l, [key]: value }
          }
          return l
        })
      )
    }

    setCustomerLocations((prev) => {
      return [
        {
          name: '',
          streetAddress: '',
          city: '',
          stateOrProvince: '',
          country: 'US',
          isDefault: true,
          [key]: value,
        },
      ].concat(prev)
    })
  }
  const handleBulkLocationChange = (obj) => {
    const pl = customerLocations.find((l) => l.isDefault)

    if (pl) {
      return setCustomerLocations((prev) =>
        prev.map((l) => {
          if (l.isDefault) {
            return { ...l, ...obj }
          }
          return l
        })
      )
    }

    setCustomerLocations((prev) => {
      return [
        {
          name: '',
          streetAddress: '',
          city: '',
          stateOrProvince: '',
          country: 'US',
          isDefault: true,
          ...obj,
        },
      ].concat(prev)
    })
  }

  const customerStatusTypes = customerStatusTypesData?.customerStatusTypes || []

  const customerPipelineSummary = useCustomerPipelineSummary(id)

  const { companyConfigStoreCopy: companyConfig, ..._companyConfig } =
    useCompanyConfig()

  const updateCustomer = (key, e, isNumber) => {
    setCustomer({
      ...customer,
      [key]: isNumber ? Number(e.target.value) : e.target.value,
    })
  }

  const saveAll = (options = {}) => {
    const { allowDuplicateName } = options
    setSubmitting(true)
    return saveCustomer({
      saveCustomerInfo: true,
      saveCustomFields: true,
      currentOpportunity,
      allowDuplicateName,
    })
      .then(({ data }) => {
        if (data.customer.error === 'DUPLICATE_NAME') {
          throw 'DUPLICATE_NAME'
        }

        setIsDirty(false)
        const promises = []
        promises.push(saveContacts(data.customer.id))
        if (currentOpportunity)
          promises.push(saveCurrentOpportunity(data.customer.id))
        promises.push(saveCustomerLocations(data.customer.id))
        return Promise.all(promises).then(() => data.customer.id)
      })

      .then((id) => {
        if (isNew) navigate(`/customers/${id}`)
        createAlert({ message: 'Profile saved successfully' })
      })

      .then(() => {
        if (isNew) return
        customerPipelineSummary.refetch()
        setCustomerCopy(JSON.stringify(customer))

        if (isCreatingOpportunity) setIsCreatingOpportunity(false)

        return Promise.all([getCustomerOpportunities(), getCustomer(false)])
      })

      .catch((e) => {
        if (e === 'DUPLICATE_NAME') {
          return setDuplicateName(true)
        }
        createGenericErrorAlert()
      })
      .finally(() => {
        setSubmitting(false)
        setIsDirty(false)
        setUpdateCounter((prev) => prev + 1)
      })
  }

  const handleCreateNewTicket = () => {
    const ticketsTab = tabs.find((t) => t.label === 'Tickets')
    const index = tabs.indexOf(ticketsTab)
    setTab(index)
    navigate({
      pathname: location.pathname,
      search: `?ticketId=new`,
    })
  }

  const headerActions = useMemo(() => {
    const actions = []

    if (!isCreatingOpportunity && !currentOpportunity) {
      actions.push({
        label: 'Create a new opportunity',
        onClick: () => setIsCreatingOpportunity(true),
        endIcon: <Lightbulb />,
      })
    }
    if (!isNew) {
      actions.push({
        label: 'Configure pipelines',
        onClick: () => setIsSelectingPipeline(true),
      })
    }

    if (!isNew) {
      actions.push({
        label: 'Create a ticket',
        onClick: handleCreateNewTicket,
      })

      actions.push({
        label: 'Delete this account',
        onClick: () => setIsDeletingCustomer(true),
        color: 'warn',
      })
    }

    return actions
  }, [isCreatingOpportunity, currentOpportunity, isNew])

  usePrompt(
    'You have unsaved changes. Would you like to continue without saving?',
    isDirty
  )

  const [updateCounter, setUpdateCounter] = useState(0)

  const [menuOpen, setMenuOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const handleCloseMenu = () => {
    setMenuOpen(false)
  }

  const [debouncedCustomer, setDebouncedCustomer] = useState(customer)

  const updateDebounce = useCallback(
    debounce((val) => {
      setDebouncedCustomer(val)
    }, 500),
    []
  )

  useEffect(() => {
    updateDebounce(customer)
  }, [customer])

  const closeOpportunityProp = (args) => {
    closeOpportunity(args).then((res) => {
      customerPipelineSummary.refetch()
      setUpdateCounter((prev) => prev + 1)
      return res
    })
  }

  const onSaveOpportunityProp = (...args) =>
    saveCurrentOpportunity(...args)
      .then(customerPipelineSummary.refetch)
      .then(getCustomerOpportunities)
      .then(() => {
        setIsCreatingOpportunity(false)
        setUpdateCounter((prev) => prev + 1)
      })

  const onCancelOpportunityProp = () => setIsCreatingOpportunity(false)

  const deleteOpportunityProp = (...args) => {
    deleteOpportunity(...args)
      .then(customerPipelineSummary.refetch)
      .then(() => setUpdateCounter((prev) => prev + 1))
  }

  const updateProductOfInterestProp = (productOfInterest) => {
    setCustomer((prev) => ({ ...prev, productOfInterest }))
  }

  if (
    loading ||
    _customer.isLoading ||
    _customerContacts.isLoading ||
    _companyConfig.isLoading ||
    !customer
  )
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )

  if (_customer.hasLoadError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <Alert severity='error'>
          Something went wrong trying to load this customer's info.
        </Alert>
      </Box>
    )
  }

  const hasNotification = customer?.notifications?.length > 0

  const markNotificationComplete = (id) => {
    return authedAxios
      .put(`/api/notifications/${id}/mark-complete`)
      .then(() => {
        setCustomer((c) => ({ ...c, notifications: [] }))
      })
  }

  const handleArchiveCustomer = () => {
    setIsDeletingCustomer(false)
    archiveCustomer()
      .then(() => {
        navigate('/customers?preventRedirect=true')
        createAlert({ message: `Successfully deleted ${customer.name}` })
      })
      .catch(() => {
        createGenericErrorAlert()
      })
  }

  const isPartner = customer.typeId === 3

  const saveIsDisabled =
    !customer.name ||
    (!isPartner && !customer.statusTypeId) ||
    currentOpportunity?.products?.length === 0

  const renderedSaveButton = (
    <SaveButton
      isNew={isNew}
      saveIsDisabled={saveIsDisabled}
      submitting={submitting}
      saveAll={saveAll}
    />
  )

  return (
    <Box>
      {hasNotification && (
        <Box sx={{ my: 3 }}>
          <Alert
            sx={{ alignItems: 'center' }}
            severity='warning'
            variant='outlined'
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={'outlined'}
                  onClick={() => {
                    setIsDeletingCustomer(true)
                  }}
                >
                  Delete
                </Button>
                <Button
                  sx={{ position: 'relative' }}
                  variant={'contained'}
                  onClick={() =>
                    markNotificationComplete(customer.notifications[0].id)
                  }
                >
                  Accept
                </Button>
              </Box>
            }
          >
            Created by lead import on{' '}
            {new Date(customer.notifications[0].createdOn).toLocaleDateString(
              'sv'
            )}
          </Alert>
          {customer.customerNote && (
            <Alert severity='info' variant='outlined' sx={{ mt: 2 }}>
              <AlertTitle>Note from customer</AlertTitle>
              {customer.customerNote}
            </Alert>
          )}
        </Box>
      )}

      <PipelineSummary
        customerPipelineSummary={customerPipelineSummary}
        customer={customer}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '1250px',
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          <CustomerHeader
            customer={customer}
            customerStatusTypesData={customerStatusTypesData}
            customerTicketsQuery={customerTicketsQuery}
            customerContacts={customerContacts}
            openContact={() =>
              setExpandedAccordions((prev) => ({ ...prev, contacts: true }))
            }
            showContact
            tasks={tasks}
            types={types}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              mt: -1,
              mr: -2,
              mb: 2,
              justifyContent: 'flex-end',
            }}
          >
            {Object.values(expandedAccordions).some((v) => v) && (
              <Button sx={{ mt: 1, mr: 2 }} onClick={collapseAll}>
                Collapse all
              </Button>
            )}
            <Button
              sx={{ mt: 1, mr: 2 }}
              variant={'outlined'}
              {...headerActions[0]}
            >
              {headerActions[0]?.label}
            </Button>

            {renderedSaveButton}

            {headerActions.length > 1 && (
              <>
                <IconButton
                  sx={{ mt: 1, mr: 2 }}
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget)
                    setMenuOpen(true)
                  }}
                >
                  <MoreHoriz />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  id='account-menu'
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  onClick={handleCloseMenu}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {headerActions.slice(1, headerActions.length).map((a, i) => (
                    <MenuItem key={i} {...a}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        {!_customerOpportunities.isLoading && currentOpportunity && (
          <CustomerOpportunity
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
            accountOwners={debouncedCustomer.accountOwners}
            setIsDirty={setIsDirty}
            pipelines={pipelines}
            key={`${id}-opportunity`}
            customer={debouncedCustomer}
            isNewOpportunity={isCreatingOpportunity}
            isNewCustomer={isNew}
            onCancel={onCancelOpportunityProp}
            currentOpportunity={currentOpportunity}
            setCurrentOpportunity={setCurrentOpportunity}
            getCustomer={getCustomer}
            onSave={onSaveOpportunityProp}
            saveOpportunity={saveCurrentOpportunity}
            resetOpportunity={_customerOpportunities.reset}
            closeOpportunity={closeOpportunityProp}
            products={products}
            billingCycleTypes={billingCycleTypes}
            onDelete={deleteOpportunityProp}
            dropdowns={dropdowns}
            updateProductOfInterest={updateProductOfInterestProp}
            // renderedSaveButton={renderedSaveButton}
          />
        )}

        <CustomerInfo
          expandedAccordions={expandedAccordions}
          setExpandedAccordions={setExpandedAccordions}
          // key={`${id}-info`}
          updateCustomer={updateCustomer}
          setCustomer={setCustomer}
          customer={customer}
          types={types}
          dropdowns={dropdowns}
          isNew={isNew}
          saveCustomer={saveCustomer}
          reset={_customer.reset}
          customerStatusTypes={customerStatusTypes}
          companyConfig={companyConfig}
          handleLocationChange={handleLocationChange}
          handleBulkLocationChange={handleBulkLocationChange}
          primaryLocation={primaryLocation}
          renderedSaveButton={renderedSaveButton}
        />

        <ContactList
          expandedAccordions={expandedAccordions}
          setExpandedAccordions={setExpandedAccordions}
          setCustomerContacts={setCustomerContacts}
          contacts={customerContacts}
          customer={customer}
          commitContact={commitContact}
          removeContact={removeContact}
          saveContacts={saveContacts}
          isNew={isNew}
          reset={_customerContacts.reset}
          handleCreateNewTicket={handleCreateNewTicket}
          renderedSaveButton={renderedSaveButton}
        />

        {customFields?.length > 0 && (
          <CustomFields
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
            key={`${id}-custom-fields`}
            customFields={customFields}
            customFieldTypes={customFieldTypes}
            customer={customer}
            setCustomer={setCustomer}
            saveCustomer={saveCustomer}
            isNew={isNew}
            reset={_customer.reset}
            renderedSaveButton={renderedSaveButton}
          />
        )}

        {!_customerLocations.isLoading && (
          <CustomerLocations
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
            key={`${id}-locations`}
            customerLocations={customerLocations}
            saveCustomerLocations={saveCustomerLocations}
            setCustomerLocations={setCustomerLocations}
            reset={_customerLocations.reset}
            renderedSaveButton={renderedSaveButton}
            deleteLocation={deleteLocation}
          />
        )}

        {!isNew && (
          <CustomerRevenueSummary
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
            customer={customer}
            updateCounter={updateCounter}
          ></CustomerRevenueSummary>
        )}
      </Box>

      {isSelectingPipeline && (
        <PipleineSelectionModal
          key={`${id}-pipelines`}
          open={isSelectingPipeline}
          onSubmit={() => setIsSelectingPipeline(false)}
          onClose={() => setIsSelectingPipeline(false)}
          customer={customer}
          pipelines={pipelines}
          customerPipelineSummary={customerPipelineSummary}
        />
      )}

      {isDeletingCustomer && (
        <DeleteConfirmModal
          isOpen={isDeletingCustomer}
          deleteString={customer.name}
          onClose={() => setIsDeletingCustomer(false)}
          onSubmit={handleArchiveCustomer}
        ></DeleteConfirmModal>
      )}

      {duplicateName && (
        <DuplicateNameModal
          open={duplicateName}
          name={customer.name}
          onSave={() => {
            setDuplicateName(false)
            saveAll({ allowDuplicateName: true })
          }}
          onClose={() => {
            setDuplicateName(false)
          }}
        ></DuplicateNameModal>
      )}
    </Box>
  )
}

export default CustomerDetail
